import { isTauri } from '../utils/tauri';
import { callNativePrint, isWindows } from '../utils/nativePrint';
import { parseEmbedSrc, videoEmbedWatchUrl } from '../utils/videoEmbeds';
import { resolveExportBodyHtml } from '../utils/resolveDocMediaForExport';

/**
 * PDF export via the WebView's native print-to-PDF (WebView2 / WKWebView both
 * expose "Save as PDF" from the print dialog). We render the document's
 * sanitized HTML into a full-screen, print-only overlay, call window.print(),
 * then tear it down. A scoped `@media print` stylesheet hides the whole app and
 * shows only the overlay, so the exported PDF is just the clean document —
 * zero new dependencies.
 */

const STYLE_ID = 'apiboo-doc-print-style';
const OVERLAY_ID = 'apiboo-doc-print-overlay';

const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  #${OVERLAY_ID}, #${OVERLAY_ID} * { visibility: visible !important; }
  #${OVERLAY_ID} {
    position: absolute !important; left: 0; top: 0; width: 100%;
    padding: 0; margin: 0; background: #fff; color: #111;
    /* Reset the on-screen hidden state so the content actually prints. */
    opacity: 1 !important; z-index: 2147483647 !important; overflow: visible !important;
  }
  @page { margin: 18mm 16mm; }
}
#${OVERLAY_ID} {
  position: fixed; inset: 0; z-index: -1; opacity: 0; pointer-events: none;
  background: #fff; color: #111; overflow: hidden;
}
#${OVERLAY_ID} .apiboo-doc-print-title {
  font-size: 26px; font-weight: 700; margin: 0 0 14px; line-height: 1.25;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
#${OVERLAY_ID} .apiboo-doc-print-body {
  font-size: 14px; line-height: 1.6;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
#${OVERLAY_ID} .apiboo-doc-print-body h1 { font-size: 22px; margin: 18px 0 8px; }
#${OVERLAY_ID} .apiboo-doc-print-body h2 { font-size: 18px; margin: 16px 0 8px; }
#${OVERLAY_ID} .apiboo-doc-print-body h3 { font-size: 16px; margin: 14px 0 6px; }
#${OVERLAY_ID} .apiboo-doc-print-body p { margin: 8px 0; }
#${OVERLAY_ID} .apiboo-doc-print-body ul,
#${OVERLAY_ID} .apiboo-doc-print-body ol { margin: 8px 0; padding-left: 24px; }
#${OVERLAY_ID} .apiboo-doc-print-body pre {
  background: #f4f4f5; border: 1px solid #e4e4e7; border-radius: 6px;
  padding: 10px 12px; overflow: auto; font-size: 12.5px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  white-space: pre-wrap; word-break: break-word;
}
#${OVERLAY_ID} .apiboo-doc-print-body code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 0.92em;
}
#${OVERLAY_ID} .apiboo-doc-print-body blockquote {
  margin: 10px 0; padding-left: 12px; border-left: 3px solid #d4d4d8; color: #52525b;
}
/* Callouts (CalloutNode): a bordered box, like the PDF export draws them. */
#${OVERLAY_ID} .apiboo-doc-print-body div[data-callout] {
  margin: 10px 0; padding: 10px 12px; border: 1px solid #e4e4e7; border-radius: 8px; background: #fafafa;
}
/* TABLES. The overlay styled headings, lists, code and quotes and simply had
   NO table rules, so a printed table came out as unaligned text with no grid —
   reported as "the print does not render the table". These mirror the editor's
   own table styling (LexicalRichEditor.css) rather than inventing a second
   look, and mirror what the PDF export draws: hairline grid, tinted header
   row, full width. */
#${OVERLAY_ID} .apiboo-doc-print-body table {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0;
  table-layout: fixed;
  /* Splitting a row across a page break loses the cell borders on both halves
     in every engine we target; a whole row moving down is the lesser evil. */
  page-break-inside: auto;
}
#${OVERLAY_ID} .apiboo-doc-print-body tr { page-break-inside: avoid; }
#${OVERLAY_ID} .apiboo-doc-print-body thead { display: table-header-group; }
#${OVERLAY_ID} .apiboo-doc-print-body th,
#${OVERLAY_ID} .apiboo-doc-print-body td {
  border: 1px solid #d4d4d8;
  padding: 5px 7px;
  vertical-align: top;
  text-align: left;
  word-break: break-word;
}
#${OVERLAY_ID} .apiboo-doc-print-body th {
  background: #f4f4f5;
  font-weight: 600;
}
/* Printing DROPS backgrounds by default. td needs this as much as th now that a
   cell can carry a user-chosen colour: it was listed for th twice and for td not
   at all, so exactly the colours somebody chose deliberately were the ones the
   printer threw away. (No backticks in here - this string is a JS template
   literal and a backtick ends it.) */
#${OVERLAY_ID} .apiboo-doc-print-body pre,
#${OVERLAY_ID} .apiboo-doc-print-body th,
#${OVERLAY_ID} .apiboo-doc-print-body td {
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
#${OVERLAY_ID} .apiboo-doc-print-body img { max-width: 100%; }
#${OVERLAY_ID} .apiboo-doc-print-body a { color: #4f46e5; text-decoration: underline; }
/* VIDEO EMBEDS. A printed iframe is a dead grey rectangle, so the overlay
   build replaces each one with the watch-URL link below; the display rule is
   the backstop for any iframe that slips through the swap. */
#${OVERLAY_ID} .apiboo-doc-print-body iframe { display: none; }
#${OVERLAY_ID} .apiboo-doc-print-body .apiboo-doc-print-video-link {
  display: block; margin: 8px 0; color: #4f46e5; text-decoration: underline;
  word-break: break-all;
}
/* CHECKLISTS. The saved HTML marks task items with aria-checked (see
   checklistRoundTrip.test.js); without these rules a printed to-do list is a
   plain bulleted list with every checked state invisible - the same silent
   loss the PDF and markdown exporters already guard against. ASCII boxes to
   match them ([x] / [ ] - no backticks or ballot-box glyphs in here). */
#${OVERLAY_ID} .apiboo-doc-print-body li[aria-checked] { list-style: none; }
#${OVERLAY_ID} .apiboo-doc-print-body li[aria-checked]::before { content: '[ ] '; }
#${OVERLAY_ID} .apiboo-doc-print-body li[aria-checked="true"]::before { content: '[x] '; }
`;

function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = PRINT_CSS;
  document.head.appendChild(style);
}

function escapeHtml(str) {
  return String(str || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

/**
 * Video embeds cannot print. Swap each iframe in the overlay for a visible
 * watch-URL link (the CSS above also hides any iframe outright, as a
 * backstop). The src is parsed and rebuilt, never echoed — same posture as
 * the sanitizer's iframe arm — and an unparseable one is simply removed.
 */
function replaceVideoEmbedsWithLinks(root) {
  for (const frame of [...root.querySelectorAll('iframe')]) {
    const parsed = parseEmbedSrc(frame.getAttribute('src') || '');
    const watchUrl = parsed ? videoEmbedWatchUrl(parsed) : null;
    if (!watchUrl) {
      frame.remove();
      continue;
    }
    const a = document.createElement('a');
    a.className = 'apiboo-doc-print-video-link';
    a.setAttribute('href', watchUrl);
    a.textContent = `▶ ${watchUrl}`;
    frame.replaceWith(a);
  }
}

/**
 * Exports a document to PDF via the print dialog.
 *
 * Async since encrypted images: the body is resolved (fetch + decrypt into
 * data: URIs, keys stripped — utils/resolveDocMediaForExport.js) BEFORE the
 * overlay is built, so the printed page shows the images instead of broken
 * `<img>` boxes. Both callers already await (DocumentEditorTab's doPrint,
 * DocumentsSidebar's withDocumentBody).
 *
 * @param {{ title?: string, bodyHtml?: string }} doc
 */
export async function exportDocumentPdf({ title = 'Untitled', bodyHtml = '' } = {}) {
  ensureStyle();

  const resolvedBody = await resolveExportBodyHtml(bodyHtml);
  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.innerHTML =
    `<div class="apiboo-doc-print-title">${escapeHtml(title || 'Untitled')}</div>` +
    `<div class="apiboo-doc-print-body">${resolvedBody}</div>`;
  replaceVideoEmbedsWithLinks(overlay);
  document.body.appendChild(overlay);

  const cleanup = () => {
    overlay.remove();
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);

  // Let layout settle before invoking the print dialog.
  requestAnimationFrame(() => {
    void openPrintDialog().catch(() => cleanup());
  });
  // Safety net in case 'afterprint' never fires (some webviews).
  setTimeout(cleanup, 60000);
}

/**
 * Open the OS print dialog by whatever route this environment actually has.
 *
 * `window.print()` is a SILENT NO-OP in WKWebView, which is what "PDF export is
 * not working" turned out to mean on macOS: the overlay was built, the call did
 * nothing, the overlay was torn down — no dialog, no error. So Tauri routes
 * through the Rust `webview_print` command instead.
 *
 * EXCEPT ON WINDOWS. wry implements the Windows side of that command as
 * `eval("window.print()")` (wry 0.55.1, src/webview2/mod.rs:1712) — it hands the
 * job straight back to this page. macOS and Linux use genuinely native paths
 * (WKWebView `printOperationWithPrintInfo:`, webkit2gtk `PrintOperation`), which
 * is why printing worked there and silently did nothing on Windows.
 *
 * An earlier version of this comment claimed Windows used WebView2's
 * `ShowPrintUI`. It does not; that was an assumption, and it is what made the
 * Windows failure hard to see.
 *
 * So on Windows the round trip is skipped and the captured native print is
 * called directly — which is exactly what wry would have evaluated, minus our
 * own no-op shadowing it.
 */
async function openPrintDialog() {
  if (isTauri() && !isWindows()) {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('webview_print');
    return;
  }
  if (!callNativePrint()) {
    throw new Error('Printing is not available in this environment.');
  }
}

/**
 * Print the document — the same overlay, the same dialog as "Export → PDF".
 *
 * REVERTED, deliberately. This briefly rendered the pdfmake document and opened
 * it in the OS viewer so printed output would match the export exactly. It did
 * not work well in practice and the owner asked for the simple path back,
 * accepting that a printed table has no grid.
 *
 * So: the overlay prints, and a table prints without borders in the packaged
 * app. That is a known, accepted limitation, not an oversight — Export → PDF is
 * the route when the table matters. The table rules in PRINT_CSS above are kept
 * because they are correct and the browser build genuinely uses this overlay;
 * they are simply inert wherever the webview ignores them.
 */
export const printDocument = exportDocumentPdf;
